import Backbone from 'backbone';
import {useEffect, useRef} from 'react';

import {
  getMainViewItemFromUrlParams,
  getUrlParamsFromSelectedItem,
  parseAnalyticsUrlQueryParameters,
} from 'analysis-tool/AnalysisToolUrls';
import {getViewForViewReference} from 'analysis-tool/data-view/AnalysisViewUtils';
import {AnalysisMainViewItem, AnalysisMainViewItemType} from 'analysis-tool/ui/main-view/MainViewTypes';
import {setURLParams} from 'BenchlingUtil';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import {dataFrameTableModeState} from 'data-frames/table/hooks/useDataFrameTableMode';
import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {getOutputDataForView, isDataFrameView} from 'pipelines/PipelineViewUtils';

import {selectedItemGlobalState} from './selectedItemGlobalState';
import {UseAnalysisUISidebarStateOutput} from './useAnalysisUISidebarState';

const NOOP_TABLE_MODE_CLIENT_ID = 'noop';

/**
 * Synchronizes the browser URL with the selected analysis main view item.
 *
 * On mount: if no item is selected in global state, reads URL params and seeds initial state.
 * On item change: updates URL query params for linkable states (FLOWCHART, DATA_VIEW, and
 * step-aware output screens like SAVE_TO_RESULTS_OUTPUT, SEND_TO_DOCUMENT, REGRESSION).
 * Transient states (RUNNING, FAILED, etc.) do not update the URL.
 *
 * Re-seeding: listens for Backbone route events and re-seeds from URL params whenever the
 * URL's deep-link params differ from what was last applied. This handles deep-link navigation
 * from notebook entries without requiring external state resets.
 *
 * Loop prevention: URL seeding is skipped when the URL params haven't changed since the last
 * seed. history.replaceState does not trigger Backbone routing.
 */
export default function useAnalysisDeepLinkSync(
  clientStateId: string,
  analysis: PipelineGraphQL,
  selectedItem: AnalysisMainViewItem,
  sidebarState: UseAnalysisUISidebarStateOutput
): void {
  // Hold sidebar callbacks in a ref so they don't need to be effect dependencies.
  const sidebarStateRef = useRef(sidebarState);
  sidebarStateRef.current = sidebarState;

  const trySeedFromUrl = useRef<() => void>(() => {});

  // URL → State: seed from URL params on mount (if no state exists) and on Backbone route
  // events when the URL params have changed (handles deep-link navigation from notebook entries).
  useEffect(() => {
    const seedFromUrl = (): void => {
      const urlParams = parseAnalyticsUrlQueryParameters();

      if (urlParams.hideSidebar && sidebarStateRef.current.isOpen) {
        sidebarStateRef.current.close();
      } else if (!urlParams.hideSidebar && !sidebarStateRef.current.isOpen) {
        sidebarStateRef.current.open();
      }

      // When we arrive here by clicking the analysis tab, urlParams are "empty" (all values are null), and
      // we want to restore the view and url from the global state.
      const existingState = selectedItemGlobalState.getState(clientStateId);
      if (Object.values(urlParams).every((v) => v == null) && existingState != null) {
        return;
      }

      // On the other hand, if we arrive here by clicking a link from a notebook entry, the urlParams are not
      // "empty", and we want to honor them.
      const itemFromUrl = getMainViewItemFromUrlParams(urlParams, analysis);
      if (itemFromUrl != null) {
        selectedItemGlobalState.updateState(clientStateId, itemFromUrl);
        // Seed table mode state so rawTableMode reflects showChips from the URL immediately,
        // before DataFrameView mounts and the toggle-action writes to dataFrameTableModeState.
        if (itemFromUrl.type === AnalysisMainViewItemType.DATA_VIEW && itemFromUrl.showChips) {
          const seedView = getViewForViewReference(analysis, itemFromUrl.viewReference);
          if (seedView != null && isDataFrameView(seedView, analysis.steps)) {
            const seedDataFrame = getOutputDataForView(
              seedView,
              analysis.steps,
              PipelineStepDataKind.DATASET
            );
            if (seedDataFrame != null) {
              dataFrameTableModeState.updateState(seedDataFrame.id, DataFrameTableMode.RICH);
            }
          }
        }
      }
    };

    trySeedFromUrl.current = seedFromUrl;
    seedFromUrl();

    const handler = (): void => {
      trySeedFromUrl.current();
    };
    Backbone.history.on('route', handler);
    return () => {
      Backbone.history.off('route', handler);
    };
  }, [clientStateId, analysis]);

  // Subscribe to the table mode for the current DATA_VIEW so URL stays in sync when the
  // user toggles chips. For non-DATA_VIEW items (or when the view isn't found), key off
  // a dummy id so the hook call is unconditional (rules of hooks).
  const view =
    selectedItem.type === AnalysisMainViewItemType.DATA_VIEW
      ? getViewForViewReference(analysis, selectedItem.viewReference)
      : null;
  // DataFrameView keys dataFrameTableModeState by the persisted data frame's ID (the raw data ID),
  // not by getDefaultClientIdForView. Match that key here so the subscription fires on toggle.
  const dataFrameRawData =
    view != null && isDataFrameView(view, analysis.steps)
      ? getOutputDataForView(view, analysis.steps, PipelineStepDataKind.DATASET)
      : null;
  const tableModeClientId = dataFrameRawData != null ? dataFrameRawData.id : NOOP_TABLE_MODE_CLIENT_ID;
  const [rawTableMode] = dataFrameTableModeState.useGlobalClientState(tableModeClientId);

  // State → URL: update URL when selected item or table mode changes
  useEffect(() => {
    const params = getUrlParamsFromSelectedItem(selectedItem);
    // Derive showChips from the actual table mode state (runtime source of truth).
    // selectedItem.showChips is only a URL→state seed on initial load.
    const showChips = rawTableMode === DataFrameTableMode.RICH ? true : null;
    // Reflect sidebar open/closed state in the URL. Use the raw isOpen flag (not the computed
    // isSidebarOpen that also masks step-init views) so auto-hide from those views doesn't
    // write hideSidebar=true for a link that should open with the sidebar visible.
    const hideSidebar = !sidebarState.isOpen ? true : null;
    // Update URL for both linkable states (non-null screen) and non-linkable states
    // (null screen) to clear stale deep-link params from the address bar.
    const newUrl = setURLParams(window.location.href, {...params, showChips, hideSidebar});
    // Only update the URL if it actually changed to avoid unnecessary replaceState calls
    // and potential re-encoding issues with special characters in query params.
    const currentUrl = window.location.pathname + window.location.search;
    if (newUrl !== currentUrl) {
      window.history.replaceState(window.history.state, '', newUrl);
    }
  }, [selectedItem, rawTableMode, sidebarState.isOpen]);
}
